// src/components/UI/DataTable.jsx
//
// The table used across Company Accounting.
//
// On top of antd's Table it adds four things the plain component has no notion
// of, all of them user-controlled and remembered between visits:
//
//   1. Drag a column header sideways to reorder it        (@dnd-kit)
//   2. Drag a column's right edge to resize it            (react-resizable)
//   3. Show/hide columns from a settings dropdown
//   4. Filtering — a global search box over every column, plus a per-column
//      filter that is built automatically from the column's own data
//
// Layout (order, widths, hidden columns) is saved to localStorage per user and
// per `persistId`, so a table you have arranged stays arranged. Without a
// `persistId` the table still works — it just forgets on reload.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Table, Input, Button, Dropdown, Checkbox, Space, Tooltip, Typography, Badge,
  DatePicker, InputNumber, Divider,
} from 'antd';
import {
  SearchOutlined, SettingOutlined, ReloadOutlined, HolderOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { Resizable } from 'react-resizable';
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove, SortableContext, horizontalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import 'react-resizable/css/styles.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/* ═══════════════ helpers ═══════════════ */

const MIN_WIDTH = 60;
/**
 * Resizing needs a numeric width, but a column the page left unsized was meant
 * to flex. 160px is a readable default; combined with the automatic horizontal
 * scroll below it stops those columns being squashed instead of stretched.
 */
const DEFAULT_WIDTH = 160;
const STORAGE_PREFIX = 'crm.tableLayout';
/** Above this many distinct values a tick-list is unusable — fall back to search. */
const MAX_TICKLIST_VALUES = 30;

const colKey = (col, index) =>
  String(col.key ?? (Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex) ?? index);

/** Read a possibly-nested dataIndex ('a' or ['a','b']) off a row. */
function readCell(row, dataIndex) {
  if (!dataIndex || !row) return undefined;
  if (!Array.isArray(dataIndex)) return row[dataIndex];
  return dataIndex.reduce((acc, k) => (acc == null ? acc : acc[k]), row);
}

/** Flatten any cell value to text so search and tick-lists can work on it. */
function cellText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) return dayjs(value).format('DD/MM/YYYY');
  if (Array.isArray(value)) return value.map(cellText).join(' ');
  if (typeof value === 'object') {
    // Populated refs are the common case here (account, branch, createdBy …).
    return cellText(value.name ?? value.code ?? value.label ?? value.title ?? '');
  }
  return '';
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;
const isDateish = (v) => v instanceof Date || (typeof v === 'string' && ISO_DATE.test(v));

/**
 * Work out how a column should be filtered from the data actually in it.
 * Numbers get a min/max box, dates a range picker, a small set of repeated
 * values a tick-list, and anything else a contains-search.
 */
function inferFilterKind(values) {
  const present = values.filter((v) => v != null && v !== '');
  if (present.length === 0) return 'none';

  if (present.every((v) => typeof v === 'number')) return 'number';
  if (present.every(isDateish)) return 'date';

  const distinct = new Set(present.map(cellText).filter(Boolean));
  if (distinct.size === 0) return 'none';
  if (distinct.size <= MAX_TICKLIST_VALUES) return 'list';
  return 'text';
}

/* ═══════════════ header cell: sortable + resizable ═══════════════ */

const ResizeHandle = React.forwardRef((props, ref) => {
  const { handleAxis, ...rest } = props;
  // Every pointer event is swallowed here so grabbing the resize grip never
  // starts a column drag or triggers the sorter.
  const swallow = (handler) => (e) => { e.stopPropagation(); if (handler) handler(e); };
  return (
    <span
      ref={ref}
      className={`react-resizable-handle react-resizable-handle-${handleAxis}`}
      {...rest}
      onClick={swallow(rest.onClick)}
      onMouseDown={swallow(rest.onMouseDown)}
      onTouchStart={swallow(rest.onTouchStart)}
      onPointerDown={swallow(rest.onPointerDown)}
      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 2 }}
    />
  );
});

const HeaderCell = (props) => {
  const { id, width, onResize, draggable, className, children, ...rest } = props;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: id || 'unknown', disabled: !draggable });

  // Placeholder/selection columns have neither an id nor a width — render plain.
  if (!id || !width) return <th {...rest} className={className}>{children}</th>;

  const style = {
    ...rest.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#e6f4ff' } : {}),
  };

  return (
    <Resizable
      width={width}
      height={0}
      minConstraints={[MIN_WIDTH, 0]}
      handle={<ResizeHandle />}
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th
        {...rest}
        ref={setNodeRef}
        style={{ ...style, position: 'relative' }}
        className={`${className || ''} crm-dt-th`}
      >
        {/* Only the grip starts a drag. Leaving the rest of the header free
            keeps antd's sorter and filter icons clickable. */}
        <span
          {...attributes}
          {...listeners}
          style={{ cursor: 'grab', marginRight: 6, opacity: 0.35, display: 'inline-block', verticalAlign: 'middle' }}
          onClick={(e) => e.stopPropagation()}
        >
          <HolderOutlined />
        </span>
        {children}
      </th>
    </Resizable>
  );
};

/* ═══════════════ per-column filter dropdowns ═══════════════ */

function TextFilter({ setSelectedKeys, selectedKeys, confirm, clearFilters, placeholder }) {
  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        autoFocus
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        style={{ width: 200, marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button type="primary" size="small" onClick={() => confirm()}>Filter</Button>
        <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Reset</Button>
      </Space>
    </div>
  );
}

function NumberFilter({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
  const [min, max] = selectedKeys[0] ? JSON.parse(selectedKeys[0]) : [null, null];
  const push = (lo, hi) =>
    setSelectedKeys(lo == null && hi == null ? [] : [JSON.stringify([lo, hi])]);
  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Space direction="vertical" size={6}>
        <InputNumber placeholder="Min" style={{ width: 200 }} value={min}
          onChange={(v) => push(v ?? null, max)} />
        <InputNumber placeholder="Max" style={{ width: 200 }} value={max}
          onChange={(v) => push(min, v ?? null)} />
        <Space>
          <Button type="primary" size="small" onClick={() => confirm()}>Filter</Button>
          <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Reset</Button>
        </Space>
      </Space>
    </div>
  );
}

function DateFilter({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
  const range = selectedKeys[0] ? JSON.parse(selectedKeys[0]) : null;
  const value = range ? [dayjs(range[0]), dayjs(range[1])] : null;
  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Space direction="vertical" size={6}>
        <RangePicker
          value={value}
          format="DD/MM/YYYY"
          onChange={(d) => setSelectedKeys(
            d && d[0] && d[1]
              ? [JSON.stringify([d[0].startOf('day').toISOString(), d[1].endOf('day').toISOString()])]
              : []
          )}
        />
        <Space>
          <Button type="primary" size="small" onClick={() => confirm()}>Filter</Button>
          <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Reset</Button>
        </Space>
      </Space>
    </div>
  );
}

/**
 * Attach a filter to a column that doesn't already define one.
 * A column the page has already configured is left completely alone.
 */
function withInferredFilter(col, rows) {
  if (col.filters || col.filterDropdown) return col;          // page knows better
  if (col.dataIndex == null) return col;                       // action column
  if (col.filterable === false) return col;                    // opt-out

  const raw = rows.map((r) => readCell(r, col.dataIndex));
  const kind = inferFilterKind(raw);
  if (kind === 'none') return col;

  const title = typeof col.title === 'string' ? col.title : 'value';

  if (kind === 'list') {
    const distinct = [...new Set(raw.map(cellText).filter((t) => t !== ''))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }));
    return {
      ...col,
      filters: distinct.map((v) => ({ text: v, value: v })),
      filterSearch: distinct.length > 10,
      onFilter: (value, record) => cellText(readCell(record, col.dataIndex)) === value,
    };
  }

  if (kind === 'number') {
    return {
      ...col,
      filterDropdown: (p) => <NumberFilter {...p} />,
      filterIcon: (filtered) => <FilterOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
      onFilter: (value, record) => {
        const [lo, hi] = JSON.parse(value);
        const n = readCell(record, col.dataIndex);
        if (typeof n !== 'number') return false;
        if (lo != null && n < lo) return false;
        if (hi != null && n > hi) return false;
        return true;
      },
    };
  }

  if (kind === 'date') {
    return {
      ...col,
      filterDropdown: (p) => <DateFilter {...p} />,
      filterIcon: (filtered) => <FilterOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
      onFilter: (value, record) => {
        const [from, to] = JSON.parse(value);
        const d = readCell(record, col.dataIndex);
        if (!d) return false;
        const t = dayjs(d);
        return t.isValid() && !t.isBefore(dayjs(from)) && !t.isAfter(dayjs(to));
      },
    };
  }

  return {
    ...col,
    filterDropdown: (p) => <TextFilter {...p} placeholder={`Search ${title}`} />,
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) =>
      cellText(readCell(record, col.dataIndex)).toLowerCase().includes(String(value).toLowerCase()),
  };
}

/* ═══════════════ component ═══════════════ */

export default function DataTable({
  columns: sourceColumns = [],
  dataSource = [],
  /** Stable id for this table; enables layout persistence. e.g. "accounting.journal-entries" */
  persistId,
  /** Set false to drop the toolbar (search + settings + reset). */
  showToolbar = true,
  /** Set false to skip building per-column filters. */
  autoFilters = true,
  /** Extra nodes rendered on the right of the toolbar. */
  toolbarExtra = null,
  /**
   * True when the rows come one page at a time from the server. Search and the
   * per-column filters can only ever see the rows already loaded, so the table
   * says so rather than implying it searched the whole dataset.
   */
  serverPaginated = false,
  searchPlaceholder,
  ...tableProps
}) {
  const { user } = useAuth();
  const storageKey = persistId ? `${STORAGE_PREFIX}.${user?._id || 'anon'}.${persistId}` : null;

  const [search, setSearch] = useState('');
  const [layout, setLayout] = useState(null);   // { order:[key], widths:{key:px}, hidden:[key] }
  const loadedFor = useRef(null);

  /* ── load the saved layout once per table/user ── */
  useEffect(() => {
    if (!storageKey) { setLayout({ order: null, widths: {}, hidden: [] }); return; }
    if (loadedFor.current === storageKey) return;
    loadedFor.current = storageKey;
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      setLayout({
        order: Array.isArray(parsed?.order) ? parsed.order : null,
        widths: parsed?.widths && typeof parsed.widths === 'object' ? parsed.widths : {},
        hidden: Array.isArray(parsed?.hidden) ? parsed.hidden : [],
      });
    } catch {
      // A corrupt entry must never take the page down with it.
      setLayout({ order: null, widths: {}, hidden: [] });
    }
  }, [storageKey]);

  const persist = useCallback((next) => {
    setLayout(next);
    if (!storageKey) return;
    try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* quota — ignore */ }
  }, [storageKey]);

  /* ── the page's columns, keyed and given a default width ── */
  const baseColumns = useMemo(
    () => sourceColumns
      .filter(Boolean)
      .map((col, i) => ({ ...col, key: colKey(col, i), width: col.width || DEFAULT_WIDTH })),
    [sourceColumns],
  );

  /* ── global search runs first; per-column filters then work on what's left ── */
  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dataSource;
    return dataSource.filter((row) =>
      baseColumns.some((col) => {
        if (col.dataIndex == null) return false;
        return cellText(readCell(row, col.dataIndex)).toLowerCase().includes(q);
      }));
  }, [dataSource, search, baseColumns]);

  /* ── apply saved order / widths / visibility, then attach filters ── */
  const visibleColumns = useMemo(() => {
    if (!layout) return [];
    const byKey = new Map(baseColumns.map((c) => [c.key, c]));

    // Saved order first, then any column added since the layout was saved.
    const ordered = [];
    (layout.order || []).forEach((k) => { if (byKey.has(k)) { ordered.push(byKey.get(k)); byKey.delete(k); } });
    baseColumns.forEach((c) => { if (byKey.has(c.key)) ordered.push(c); });

    const hidden = new Set(layout.hidden || []);
    return ordered
      .filter((c) => !hidden.has(c.key))
      .map((c) => ({ ...c, width: layout.widths?.[c.key] || c.width }))
      // Filters are built from the search-narrowed rows so the tick-list only
      // ever offers values that are actually on screen.
      .map((c) => (autoFilters ? withInferredFilter(c, searchedRows) : c));
  }, [baseColumns, layout, autoFilters, searchedRows]);

  /* ── resize ── */
  const handleResize = useCallback((key) => (e, { size }) => {
    e.stopImmediatePropagation?.();
    persist({ ...layout, widths: { ...(layout?.widths || {}), [key]: Math.max(MIN_WIDTH, size.width) } });
  }, [layout, persist]);

  /* ── reorder ── */
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const currentOrder = visibleColumns.map((c) => c.key);
    const from = currentOrder.indexOf(active.id);
    const to = currentOrder.indexOf(over.id);
    if (from < 0 || to < 0) return;
    const movedVisible = arrayMove(currentOrder, from, to);
    // Hidden columns keep their relative place at the end of the saved order,
    // so unhiding one later doesn't jump it to the front.
    const hidden = (layout?.hidden || []).filter((k) => baseColumns.some((c) => c.key === k));
    persist({ ...layout, order: [...movedVisible, ...hidden] });
  };

  /* ── show / hide ── */
  const toggleColumn = (key, checked) => {
    const hidden = new Set(layout?.hidden || []);
    if (checked) hidden.delete(key); else hidden.add(key);
    persist({ ...layout, hidden: [...hidden] });
  };

  const resetLayout = () => {
    setSearch('');
    const cleared = { order: null, widths: {}, hidden: [] };
    setLayout(cleared);
    if (storageKey) { try { window.localStorage.removeItem(storageKey); } catch { /* ignore */ } }
  };

  const placeholder = searchPlaceholder
    || (serverPaginated ? 'Search loaded rows…' : 'Search all columns…');

  const isCustomised =
    !!(layout && ((layout.order && layout.order.length) ||
      Object.keys(layout.widths || {}).length || (layout.hidden || []).length));

  const mergedColumns = visibleColumns.map((col) => ({
    ...col,
    onHeaderCell: (column) => ({
      id: column.key,
      width: column.width,
      draggable: true,
      onResize: handleResize(column.key),
    }),
  }));

  // Once every column carries a width, the table must be allowed to scroll to
  // at least their sum — otherwise antd compresses them and a resize appears to
  // do nothing. A scroll the page set itself always wins.
  const totalWidth = mergedColumns.reduce((sum, c) => sum + (Number(c.width) || DEFAULT_WIDTH), 0);
  const scroll = tableProps.scroll ?? { x: totalWidth };

  if (!layout) return null;   // first paint waits for the saved layout

  const settingsMenu = (
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,.12)', padding: 12, maxHeight: 380, overflowY: 'auto' }}>
      <Text strong style={{ fontSize: 12 }}>Columns</Text>
      <Divider style={{ margin: '8px 0' }} />
      <Space direction="vertical" size={4}>
        {baseColumns.map((c) => (
          <Checkbox
            key={c.key}
            checked={!(layout.hidden || []).includes(c.key)}
            onChange={(e) => toggleColumn(c.key, e.target.checked)}
          >
            {typeof c.title === 'string' ? c.title : c.key}
          </Checkbox>
        ))}
      </Space>
      <Divider style={{ margin: '8px 0' }} />
      <Text type="secondary" style={{ fontSize: 11 }}>
        Drag the <HolderOutlined /> grip in a header to reorder · drag a column edge to resize
      </Text>
    </div>
  );

  return (
    <div>
      {showToolbar && (
        <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }} wrap>
          <Tooltip
            title={serverPaginated
              ? 'This table loads one page at a time — search and column filters apply to the rows currently loaded, not the whole dataset.'
              : 'Searches every column in the table.'}
          >
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280 }}
              size="small"
            />
          </Tooltip>
          <Space size={4}>
            {toolbarExtra}
            {search && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {searchedRows.length} of {dataSource.length}
              </Text>
            )}
            <Dropdown popupRender={() => settingsMenu} trigger={['click']} placement="bottomRight">
              <Tooltip title="Show / hide columns">
                <Badge dot={(layout.hidden || []).length > 0}>
                  <Button size="small" icon={<SettingOutlined />} />
                </Badge>
              </Tooltip>
            </Dropdown>
            <Tooltip title={isCustomised ? 'Reset column order, widths and visibility' : 'Layout is unchanged'}>
              <Button size="small" icon={<ReloadOutlined />} onClick={resetLayout} disabled={!isCustomised && !search} />
            </Tooltip>
          </Space>
        </Space>
      )}

      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        collisionDetection={closestCenter}
      >
        <SortableContext items={mergedColumns.map((c) => c.key)} strategy={horizontalListSortingStrategy}>
          <Table
            {...tableProps}
            scroll={scroll}
            dataSource={searchedRows}
            columns={mergedColumns}
            components={{ header: { cell: HeaderCell } }}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
}
